import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rolesApi, type RoleDetail } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Lock, Plus, Pencil, Trash2 } from 'lucide-react';
import { QueryState } from '@/components/query-state';
import { CreateRoleDialog } from '@/components/create-role-dialog';
import { EditRoleDialog } from '@/components/edit-role-dialog';
import { DeleteRoleDialog } from '@/components/delete-role-dialog';
import { useAuth } from '@/lib/auth-context';

function RolesSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-6 w-32 bg-muted rounded" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 w-48 bg-muted rounded" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function RolesPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes('Admin') ?? false;

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDetail | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleDetail | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });

  const roles = data?.data || [];
  const builtIns = roles.filter(r => r.is_system);
  const customs = roles.filter(r => !r.is_system);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <span className="rf3-eyebrow">Administration</span>
          <h2 className="text-3xl font-bold tracking-tight text-primary">Roles</h2>
          <p className="text-muted-foreground">
            Built-in roles apply suite-wide. Custom roles are visible only within your tenant.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New role
          </Button>
        )}
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        skeleton={<RolesSkeleton />}
      >
        <div className="space-y-8">
          <section>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Built-in roles
            </h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {builtIns.map(role => (
                <RoleCard key={role.id} role={role} />
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Custom roles
                <span className="text-sm font-normal text-muted-foreground">
                  ({customs.length})
                </span>
              </h3>
            </div>
            {customs.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No custom roles yet.{' '}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setCreateOpen(true)}
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        Create one
                      </button>
                    )}
                    {!isAdmin && 'An admin can create one from this page.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {customs.map(role => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    onEdit={isAdmin ? () => setEditRole(role) : undefined}
                    onDelete={isAdmin ? () => setDeleteRole(role) : undefined}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </QueryState>

      <CreateRoleDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditRoleDialog
        role={editRole}
        open={editRole !== null}
        onOpenChange={(open) => !open && setEditRole(null)}
      />
      <DeleteRoleDialog
        role={deleteRole}
        open={deleteRole !== null}
        onOpenChange={(open) => !open && setDeleteRole(null)}
      />
    </div>
  );
}

interface RoleCardProps {
  role: RoleDetail;
  onEdit?: () => void;
  onDelete?: () => void;
}

function RoleCard({ role, onEdit, onDelete }: RoleCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-lg truncate">{role.name}</CardTitle>
            <p className="text-xs font-mono text-muted-foreground truncate">{role.key}</p>
          </div>
          {role.is_system && (
            <Lock className="h-4 w-4 text-muted-foreground shrink-0" aria-label="Built-in role" />
          )}
        </div>
        {role.description && <CardDescription>{role.description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-sm font-medium mb-2">
            Permissions ({role.permissions.length})
          </p>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {role.permissions.length === 0 ? (
              <span className="text-xs text-muted-foreground">No permissions</span>
            ) : (
              role.permissions.map(action => (
                <span
                  key={action}
                  className="px-2 py-0.5 bg-muted text-xs rounded font-mono"
                >
                  {action}
                </span>
              ))
            )}
          </div>
        </div>
        {(onEdit || onDelete) && (
          <div className="flex gap-2 pt-1">
            {onEdit && (
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="outline" onClick={onDelete}>
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
